import type { PaginatedListState, PaginatedStateAdapter, PaginatedStatePatch } from '@om1/aspen-api/codegen/hooks/usePaginatedData'
import { getItem, setItem } from '@om1/platform-components/localStorage'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/**
 * Query-param keys this hook owns. Anything else in the querystring — notably
 * ``referrer`` and ``referrerName``, which the dashboard links in with — is
 * merged through untouched.
 */
const PAGE_KEY = 'page'
const LIMIT_KEY = 'limit'
const SORT_KEY = 'sort'
const SEARCH_KEY = 'q'
const FILTER_PREFIX = 'f.'

const OWNED_KEYS = [PAGE_KEY, LIMIT_KEY, SORT_KEY, SEARCH_KEY]

const storageKeyFor = (namespace: string) => `list-state-${namespace}`

const parseIntOr = (value: string | null, fallback: number): number => {
    const parsed = Number.parseInt(value ?? '', 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/** Does the querystring carry any state this hook owns? */
const hasOwnedParams = (params: URLSearchParams): boolean =>
    OWNED_KEYS.some((key) => params.has(key)) || Array.from(params.keys()).some((key) => key.startsWith(FILTER_PREFIX))

const readStateFromParams = (params: URLSearchParams, defaults: PaginatedListState): PaginatedListState => {
    const filters: Record<string, string> = {}
    params.forEach((value, key) => {
        if (key.startsWith(FILTER_PREFIX)) {
            // An empty value is meaningful: it records a filter the user
            // deliberately cleared, which must not be re-defaulted.
            filters[key.slice(FILTER_PREFIX.length)] = value
        }
    })

    return {
        page: parseIntOr(params.get(PAGE_KEY), defaults.page),
        limit: parseIntOr(params.get(LIMIT_KEY), defaults.limit),
        sort: params.get(SORT_KEY) ?? defaults.sort,
        search: params.get(SEARCH_KEY) ?? '',
        filters
    }
}

const writeStateToParams = (state: PaginatedListState, existing: URLSearchParams): URLSearchParams => {
    const next = new URLSearchParams(existing)

    Array.from(next.keys())
        .filter((key) => OWNED_KEYS.includes(key) || key.startsWith(FILTER_PREFIX))
        .forEach((key) => next.delete(key))

    next.set(PAGE_KEY, String(state.page))
    next.set(LIMIT_KEY, String(state.limit))
    if (state.sort) next.set(SORT_KEY, state.sort)
    if (state.search) next.set(SEARCH_KEY, state.search)
    Object.entries(state.filters).forEach(([key, value]) => next.set(`${FILTER_PREFIX}${key}`, value))

    return next
}

/**
 * Keeps list state in the URL, layered over localStorage.
 *
 * Precedence on landing:
 *   1. the URL, whenever it carries any owned param — a shared link has to open
 *      exactly what the sender saw, never blended with the recipient's history;
 *   2. otherwise the last state stored for this list, replaced into the URL so
 *      the address bar and the request never disagree;
 *   3. otherwise ``defaults``.
 *
 * Every change is a ``history.replace``: filter tweaks and debounced keystrokes
 * must not become history entries, or Back walks the coordinator through their
 * own typing instead of returning them to the list. Navigating to a detail page
 * stays a ``push``, so Back lands here with this state intact.
 */
export const useListUrlState = (namespace: string, defaults: PaginatedListState): PaginatedStateAdapter => {
    const history = useHistory()
    const location = useLocation()
    const storageKey = storageKeyFor(namespace)

    const params = useMemo(() => new URLSearchParams(location.search), [location.search])

    const resolvedState = useMemo(() => {
        if (hasOwnedParams(params)) {
            return readStateFromParams(params, defaults)
        }

        // A URL carrying anything at all was constructed deliberately — the
        // dashboard's "Candidates by Referrer" link is ``?referrer=…`` — so the
        // caller's intent beats this browser's history. ``defaults`` already
        // folds that referrer into its filters; restoring stored state here
        // would replace the filters wholesale and drop it, leaving the search
        // box naming a referrer the list is not filtered by.
        if (Array.from(params.keys()).length > 0) {
            return defaults
        }

        const stored = getItem<PaginatedListState>(storageKey)
        // Replace filters rather than merging them: a filter the user cleared
        // is absent from storage, and merging would resurrect it from defaults.
        return stored ? { ...defaults, ...stored } : defaults
        // ``defaults`` is rebuilt each render by callers; keying on its content
        // rather than identity avoids resetting state on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, storageKey, JSON.stringify(defaults)])

    // Hand back the same object while the content is unchanged. Seeding the URL
    // re-runs the memo above and produces a structurally identical state; a
    // fresh identity there would re-key ``fetchData`` in usePaginatedData and
    // fire a second, identical request on every bare landing.
    const stableStateRef = useRef<{ key: string; value: PaginatedListState }>()
    const resolvedKey = JSON.stringify(resolvedState)
    if (!stableStateRef.current || stableStateRef.current.key !== resolvedKey) {
        stableStateRef.current = { key: resolvedKey, value: resolvedState }
    }
    const state = stableStateRef.current.value

    // Tracks the newest querystring we know about. Kept in a ref rather than
    // read back off the router so two changes landing in the same tick see each
    // other's writes, which a re-render has not yet published.
    const latestSearchRef = useRef(location.search)
    latestSearchRef.current = location.search

    const replaceSearch = useCallback(
        (nextParams: URLSearchParams) => {
            const search = `?${nextParams.toString()}`
            latestSearchRef.current = search
            history.replace({ search })
        },
        [history]
    )

    // Seed the URL once when it arrived bare, so what is on screen is always
    // spelled out in the address bar and is therefore shareable. Done in an
    // effect: navigating during render is a side effect React does not allow,
    // and it would throw rather than degrade.
    const didSeedRef = useRef(false)
    useEffect(() => {
        if (didSeedRef.current || hasOwnedParams(params)) return
        didSeedRef.current = true
        replaceSearch(writeStateToParams(state, params))
    }, [params, replaceSearch, state])

    const setState = useCallback(
        (patch: PaginatedStatePatch) => {
            const currentParams = new URLSearchParams(latestSearchRef.current)
            const current = hasOwnedParams(currentParams) ? readStateFromParams(currentParams, defaults) : state
            const next: PaginatedListState = { ...current, ...(typeof patch === 'function' ? patch(current) : patch) }

            setItem(storageKey, next)
            replaceSearch(writeStateToParams(next, currentParams))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [replaceSearch, storageKey, state, JSON.stringify(defaults)]
    )

    return { state, setState }
}
